import * as Sentry from "@sentry/react";
import axios from "axios";
import copy from "copy-to-clipboard";
import { format } from "date-fns";

import {
  isProviderPortalRequest,
  isPublicAuthRequest,
  isWorkspaceScopedRequest,
} from "app/common/workspace/requestScope";

export const vanillaAxios = axios.create();

export const SERVER_URL = import.meta.env.VITE_API_URL;

export const PASSWORD_REGEX = /(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&\-_|\\:;"'<,>.?/[\]{}`~])/;

export function removeCSSClass(ele, cls) {
  const reg = new RegExp("(\\s|^)" + cls + "(\\s|$)");
  ele.className = ele.className.replace(reg, " ");
}

export function addCSSClass(ele, cls) {
  ele.classList.add(cls);
}

export const toAbsoluteUrl = (pathname) => (import.meta.env.BASE_URL || "/") + pathname;

export function setupAxios(axios, isMocked) {
  if (!isMocked) {
    axios.defaults.baseURL = SERVER_URL;

    if (import.meta.env.VITE_RATE_LIMIT_BYPASS_SECRET) {
      axios.defaults.headers[import.meta.env.VITE_RATE_LIMIT_BYPASS_HEADER_NAME] =
        import.meta.env.VITE_RATE_LIMIT_BYPASS_SECRET;
    }
  }

  axios.interceptors.request.use(
    (config) => {
      config.headers = config.headers || {};
      const authToken = getStorage("token");
      const isProviderPortalRequestUrl = isProviderPortalRequest(config.url);
      const hasMfaSetupToken = Boolean(config.headers["X-MFA-Setup-Token"]);

      // Provider links authenticate with their own request/provider credentials. Sending the
      // workspace user's token can cause the API to reject otherwise valid portal links. MFA
      // setup tokens are similarly exclusive: the API must receive the one-time setup token,
      // not an existing session token.
      if (
        authToken &&
        !isPublicAuthRequest(config.url) &&
        !isProviderPortalRequestUrl &&
        !hasMfaSetupToken
      ) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      if (isWorkspaceScopedRequest(config.url)) {
        const workspaceId = getStorage("active-workspace-id:v1");
        if (workspaceId) {
          config.headers.WORKSPACEID = workspaceId;
        }
      }

      return config;
    },
    (err) => Promise.reject(err)
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      console.error(err);

      const status = err && err.response && err.response.status;
      const isWorkspaceAccessError =
        isWorkspaceScopedRequest(err?.config?.url) && [401, 403].includes(status);
      const isProviderPortalRequestUrl = isProviderPortalRequest(err?.config?.url);

      // 405 is what this API has always returned for an expired session, and is
      // treated as such unconditionally to preserve existing behaviour. 401 is
      // the standard status for it, so it is honoured too — but only when a
      // token actually exists, otherwise a 401 from the login endpoint itself
      // would replace the "invalid credentials" message with a session-timeout
      // redirect.
      const isSessionExpired =
        !isProviderPortalRequestUrl &&
        !isWorkspaceAccessError &&
        (status === 405 || (status === 401 && !!getStorage("token")));

      if (isWorkspaceAccessError) {
        window.dispatchEvent(new CustomEvent("workspace-access-error", { detail: { status } }));
      }

      if (isSessionExpired) {
        removeStorage("token");
        removeStorage("active-workspace-id:v1");
        let errorMessage =
          err.response.data && err.response.data.error && err.response.data.error.message;

        errorMessage = errorMessage || "Session timed out, Please log-in again.";

        window.location = "/auth/login?error=" + encodeURIComponent(errorMessage);
      }

      return Promise.reject(err);
    }
  );
}

export function setupSentry() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!sentryDsn) {
    console.warn("VITE_SENTRY_DSN is not set. Sentry error tracking will not be initialized.");
    return;
  }

  const environment = import.meta.env.VITE_ENV;

  Sentry.init({
    dsn: sentryDsn,
    integrations: [Sentry.browserTracingIntegration()],
    environment,

    // Performance traces are billed per transaction, so production samples a
    // slice rather than every page load and navigation. Non-production traffic
    // is low volume and worth capturing in full. This only affects performance
    // transactions — errors are always reported.
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
  });
}

/*  removeStorage: removes a key from localStorage and its sibling expiracy key
    params:
        key <string>     : localStorage key to remove
    returns:
        <boolean> : telling if operation succeeded
 */
export function removeStorage(key) {
  try {
    localStorage.setItem(key, "");
  } catch (e) {
    console.error(
      "removeStorage: Error removing key [" + key + "] from localStorage: " + JSON.stringify(e)
    );
    return false;
  }
  return true;
}

/*  getStorage: retrieves a key from localStorage previously set with setStorage().
    params:
      key <string> : localStorage key
    returns:
      <string> : value of localStorage key
      null : in case of expired key or failure
 */
export function getStorage(key) {
  try {
    const value = localStorage.getItem(key);
    return value;
  } catch (e) {
    console.error(
      "getStorage: Error reading key [" + key + "] from localStorage: " + JSON.stringify(e)
    );
    return null;
  }
}
/*  setStorage: writes a key into localStorage setting a expire time
    params:
      key <string>     : localStorage key
      value <string>   : localStorage value
    returns:
      <boolean> : telling if operation succeeded
 */
export function setStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.error(
      "setStorage: Error setting key [" + key + "] in localStorage: " + JSON.stringify(e)
    );
    return false;
  }
  return true;
}

export const getFileExtension = (filename) => {
  const fragments = filename.split(".");
  const extension = fragments.pop().toUpperCase();

  return extension;
};

export const getFileIcon = (filename) => {
  const extension = filename ? getFileExtension(filename) : undefined;

  switch (extension) {
    case "PDF": {
      return "/media/files/pdf.png";
    }
    case "JPEG":
    case "PNG":
    case "TIFF":
    case "JPG": {
      return "/media/files/image.png";
    }

    default: {
      return "/media/files/file.png";
    }
  }
};

export function getFileName(filename) {
  if (!filename) return "";

  const CHAR_LIMIT = 24;
  const fragment = filename.slice(0, CHAR_LIMIT);
  return `${fragment}${filename.length > CHAR_LIMIT ? "..." : ""}`;
}

export function dataURLtoFile(dataUrl, filename) {
  var arr = dataUrl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export function isAttachmentValid(file) {
  const invalidFileExtension = [
    ".ade",
    ".adp",
    ".apk",
    ".appx",
    ".appxbundle",
    ".bat",
    ".cab",
    ".chm",
    ".cmd",
    ".com",
    ".cpl",
    ".dll",
    ".dmg",
    ".exe",
    ".hta",
    ".ins",
    ".isp",
    ".iso",
    ".jar",
    ".js",
    ".jse",
    ".lib",
    ".lnk",
    ".mde",
    ".msc",
    ".msi",
    ".msix",
    ".msixbundle",
    ".msp",
    ".mst",
    ".nsh",
    ".pif",
    ".ps1",
    ".scr",
    ".sct",
    ".shb",
    ".sys",
    ".vb",
    ".vbe",
    ".vbs",
    ".vxd",
    ".wsc",
    ".wsf",
    ".wsh",
  ];

  const isFileInvalid = invalidFileExtension.some((invalidExtension) => {
    return file.name.endsWith(invalidExtension);
  });

  return !isFileInvalid;
}

/**
 * Convert HEIC/HEIF images to PNG on client side before uploading since
 * browser do not support those image files natively
 * @param {File} file
 */
export async function sanitizeFile(file) {
  const extension = getFileExtension(file.name);

  if (["HEIF", "HEIC"].includes(extension)) {
    try {
      // Loaded on demand: heic2any is ~1 MB and only needed for the rare HEIC
      // upload, but a static import pulled it into the initial bundle because
      // this module is reachable from the app entry point.
      const { default: heic2any } = await import("heic2any");
      const CONVERT_TO_MIME_TYPE = "image/png";
      const fileBlob = await heic2any({
        blob: file,
        toType: CONVERT_TO_MIME_TYPE,
        quality: 1,
      });
      const fileNameFragments = file.name.split(".");

      // remove extension
      fileNameFragments.pop();

      let fileName = fileNameFragments.join(".");

      // add new extension
      fileName += ".png";

      file = new File([fileBlob], fileName, {
        type: CONVERT_TO_MIME_TYPE,
      });
    } catch (e) {
      console.error(e);
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      return file;
    }
  }

  return file;
}

export async function downloadLocalFile(file, filename) {
  const a = document.createElement("a");
  a.style.display = "none";
  document.body.appendChild(a);

  // Set the HREF to a Blob representation of the data to be downloaded
  a.href = window.URL.createObjectURL(file);

  // Use download attribute to set set desired file name
  a.setAttribute("download", filename);

  // Trigger the download by simulating click
  a.click();

  // Cleanup
  window.URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
}

const generateId = () =>
  Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

// since it"s bad practice to store non-serializable objects into redux store and axios
// provides no utility to get source instance from token we are maintaining that mapping
// manually in global window object;
window.AXIOS_GLOBAL_CANCEL_TOKENS = {};

export const getNewCancelToken = () => {
  const source = axios.CancelToken.source();
  source.id = generateId();
  window.AXIOS_GLOBAL_CANCEL_TOKENS[source.id] = source;

  return source;
};

export const cancelRequest = (id) => {
  const source = window.AXIOS_GLOBAL_CANCEL_TOKENS[id];

  if (source) {
    source.cancel();
  }

  delete window.AXIOS_GLOBAL_CANCEL_TOKENS[id];
};

export async function downloadFileFromUrl(url, filename, cancelTokenId = undefined, onProgress) {
  const cancelSource = cancelTokenId && window.AXIOS_GLOBAL_CANCEL_TOKENS[cancelTokenId];
  const cancelToken = cancelSource && cancelSource.token;

  try {
    const response = await vanillaAxios({
      url,
      responseType: "blob",

      cancelToken,
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      },
    });

    const fileData = new Blob([response.data]);
    downloadLocalFile(fileData, filename);
  } finally {
    if (cancelTokenId) {
      delete window.AXIOS_GLOBAL_CANCEL_TOKENS[cancelTokenId];
    }
  }
}

export async function requestSignedUrl(url) {
  return axios.post(`/services/preview-from-url`, { url });
}

export function getDummyFileRenameStrings(fileRename = []) {
  const sanitizedFileRename = fileRename.filter((fragment) => !!fragment);

  if (!sanitizedFileRename.length) {
    return [];
  }

  const fileRenameStrings = {
    "{{firstName}}": "John",
    "{{lastName}}": "Doe",
    "{{mm}}": format(new Date(), "MM"),
    "{{yyyy}}": format(new Date(), "yyyy"),
    "{{date}}": format(new Date(), "dd-MM-yyyy-HH-mm-ss"),
  };
  const fileRenameDummyString = fileRename
    .map((fragment) => fileRenameStrings[fragment] || fragment)
    .join("_");
  const extensions = ["pdf", "png"];
  const fileRenameHintStrings = extensions.map((extension, index) => {
    let appendStr = index ? `_${index}.${extension}` : `.${extension}`;
    return `${index + 1}. ${fileRenameDummyString}${appendStr}`;
  });

  return fileRenameHintStrings;
}

export function copyTextToClipboard(text) {
  copy(text);
}

export function globalGA(event, data = {}) {
  if (window.dataLayer && import.meta.env.VITE_ENV !== "local") {
    window.dataLayer.push({
      ...data,
      event,
    });
  } else {
    console.warn("Google Tag Manager is not configured", arguments);
  }
}

export function getInputSize(value) {
  const reductionOffset = value.length / 7;
  return value.length - reductionOffset;
}

export function isValidEmail(email) {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return !!email && emailRegex.test(email);
}

export function isValidUrl(urlStr) {
  const pattern = new RegExp(
    "^(https:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  );

  return pattern.test(urlStr);
}

function getElementY(query) {
  return window.pageYOffset + document.querySelector(query).getBoundingClientRect().top;
}

export function doScrolling(elementQuerySelector, duration = 1000, offset = 0) {
  var startingY = window.pageYOffset;
  var elementY = getElementY(elementQuerySelector);
  // If element is close to page's bottom then window will scroll only to some position above the element.
  var targetY =
    document.body.scrollHeight - elementY < window.innerHeight
      ? document.body.scrollHeight - window.innerHeight
      : elementY;

  targetY += offset;

  var diff = targetY - startingY;
  // Easing function: easeInOutCubic
  // From: https://gist.github.com/gre/1650294
  var easing = function (t) {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  };
  var start;

  if (!diff) return;

  // Bootstrap our animation - it will get called right before next frame shall be rendered.
  window.requestAnimationFrame(function step(timestamp) {
    if (!start) start = timestamp;
    // Elapsed miliseconds since start of scrolling.
    var time = timestamp - start;
    // Get percent of completion in range [0, 1].
    var percent = Math.min(time / duration, 1);
    // Apply the easing.
    // It can cause bad-looking slow frames in browser performance tool, so be careful.
    percent = easing(percent);

    window.scrollTo(0, startingY + diff * percent);

    // Proceed with animation as long as we wanted it to.
    if (time < duration) {
      window.requestAnimationFrame(step);
    }
  });
}

export async function base64ToFile(dataUrl, fileName, options) {
  // dataUrl is of format 'data:image/png;base6....';
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], fileName, options);
}

export function addAlpha(color, opacity) {
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

export function pickTextColor(bgColor = "#E0E0E0", lightColor = "white", darkColor = "black") {
  var color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
  var r = parseInt(color.substring(0, 2), 16); // hexToR
  var g = parseInt(color.substring(2, 4), 16); // hexToG
  var b = parseInt(color.substring(4, 6), 16); // hexToB
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? darkColor : lightColor;
}

window.globalGA = globalGA;
